const POST_AUTH_REDIRECT_KEY = 'post_auth_redirect';

const readStorageValue = (
  storage: Pick<Storage, 'getItem'> | undefined,
): string | null => {
  try {
    return storage?.getItem(POST_AUTH_REDIRECT_KEY) ?? null;
  } catch {
    return null;
  }
};

const removeStorageValue = (
  storage: Pick<Storage, 'removeItem'> | undefined,
): void => {
  try {
    storage?.removeItem(POST_AUTH_REDIRECT_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const writeStorageValue = (
  storage: Pick<Storage, 'setItem'> | undefined,
  value: string,
): void => {
  try {
    storage?.setItem(POST_AUTH_REDIRECT_KEY, value);
  } catch {
    // Ignore storage failures.
  }
};

export const buildSigninRedirectUrl = (currentPath: string): string => {
  const trimmed = currentPath.trim();
  if (!trimmed || trimmed === '/') return '/signin';
  return `/signin?redirect=${encodeURIComponent(trimmed)}`;
};

export const safeSetLocationHref = (href: string): void => {
  try {
    window.location.href = href;
  } catch {
    // In non-browser runtimes (e.g. jsdom) setting location may throw.
  }
};

export const safeReloadLocation = (): void => {
  try {
    window.location.reload();
  } catch {
    // In non-browser runtimes (e.g. jsdom) reload may throw.
  }
};

export const redirectToSignin = (
  currentPath = window.location.pathname + window.location.search,
): string => {
  const href = buildSigninRedirectUrl(currentPath);
  safeSetLocationHref(href);
  return href;
};

type NormalizePostAuthRedirectOptions = {
  extraBlockedPrefixes?: string[];
};

export const normalizePostAuthRedirect = (
  redirect: string | null,
  options?: NormalizePostAuthRedirectOptions,
): string => {
  const fallback = '/';
  if (!redirect) return fallback;

  if (!redirect.startsWith('/') || redirect.startsWith('//')) return fallback;

  const blockedPrefixes = [
    '/signin',
    '/authenticate',
    '/reset',
    '/auth/discovery-callback',
    ...(options?.extraBlockedPrefixes ?? []),
  ];

  if (
    blockedPrefixes.some(
      (p) =>
        redirect === p ||
        redirect.startsWith(`${p}?`) ||
        redirect.startsWith(`${p}/`),
    )
  ) {
    return fallback;
  }

  return redirect;
};

const readStoredPostAuthRedirectValue = (): string | null =>
  readStorageValue(window.localStorage) ??
  readStorageValue(window.sessionStorage);

export const storePostAuthRedirect = (
  redirect: string | null,
  options?: NormalizePostAuthRedirectOptions,
): void => {
  if (redirect === null) {
    removeStorageValue(window.localStorage);
    removeStorageValue(window.sessionStorage);
    return;
  }

  const normalizedRedirect = normalizePostAuthRedirect(redirect, options);
  writeStorageValue(window.localStorage, normalizedRedirect);
  writeStorageValue(window.sessionStorage, normalizedRedirect);
};

export const clearStoredPostAuthRedirect = (): void => {
  removeStorageValue(window.localStorage);
  removeStorageValue(window.sessionStorage);
};

export const resolvePostAuthRedirect = (
  redirect: string | null,
  options?: NormalizePostAuthRedirectOptions,
): string =>
  normalizePostAuthRedirect(
    redirect ?? readStoredPostAuthRedirectValue(),
    options,
  );

export const readPostAuthRedirect = (
  options?: NormalizePostAuthRedirectOptions,
): string | null => {
  const stored = readStoredPostAuthRedirectValue();
  if (stored === null) return null;
  return normalizePostAuthRedirect(stored, options);
};
